import { useEffect, useState } from 'react';
import { http } from '@/lib/api';

/**
 * "Continue with Google" button.
 *
 * Renders nothing if the backend reports Google sign-in is disabled (no
 * Client ID/Secret in the server env), so the app degrades gracefully when
 * Google is not configured on a given deployment.
 *
 * On click, we redirect the browser to /api/auth/google/start — the SERVER
 * initiates the OAuth flow (PKCE + state + nonce). We never touch a client-
 * side Google SDK, so we keep full control of the redirect + session issuance.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const DEFAULT_CLASS = 'w-full flex items-center justify-center gap-3 bg-white border border-navy/15 text-navy rounded-full py-3 font-medium hover:bg-beige/40 transition-smooth';

export default function GoogleSignInButton({ label = 'Continue with Google', nextUrl = '/app', testId = 'google-signin', role = null, className = DEFAULT_CLASS }) {
  const [enabled, setEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get('/auth/google/status');
        if (!cancelled) setEnabled(!!r.data?.enabled);
      } catch { if (!cancelled) setEnabled(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleClick = () => {
    // Server-initiated redirect — the backend generates PKCE + state + nonce.
    // window.location.origin is used to derive the redirect_uri server-side
    // from the incoming request, so it works across preview / prod / custom domain.
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const params = new URLSearchParams();
    if (nextUrl) params.set('next', nextUrl);
    if (role === 'member' || role === 'supporter') params.set('role', role);
    const qs = params.toString();
    window.location.href = `${backend}/api/auth/google/start${qs ? `?${qs}` : ''}`;
  };

  if (enabled === null) return null;   // still loading
  if (enabled === false) return null;  // Google not configured on this deployment

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={handleClick}
      className={className}
      aria-label="Continue with Google"
    >
      <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      </svg>
      <span>{label}</span>
    </button>
  );
}
